import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  FormControlLabel,
  Menu,
  MenuItem,
  Tooltip,
  Select,
  IconButton,
  Container,
  Divider,
  Button,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { BusinessContext } from '../context/BusinessContext';
import { ReviewsContext } from '../context/ReviewsContext';
import SettingsIcon from '@mui/icons-material/Settings';
import FilterListIcon from '@mui/icons-material/FilterList';
import MenuIcon from '@mui/icons-material/Menu';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useLocation, useNavigate } from 'react-router-dom';
import Rating from '@mui/material/Rating';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { colors } from '../utils/themeUtils';
import { SortOptionContext } from '../context/SortOptionProvider';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { NavigationContext } from '../components/Layout';

const timeframeOptions = [
  { value: "7", label: "Last 7 Days" },
  { value: "30", label: "Last 30 Days" },
  { value: "90", label: "Last 90 Days" },
  { value: "365", label: "Last 365 Days" },
  { value: "all", label: "All Time" },
];

const Header = () => {
  const { selectedBusiness, currencySymbol } = useContext(BusinessContext);
  const { 
    timeframe, 
    setTimeframe, 
    setSelectedDate, 
    view, 
    setView, 
    showColoredText, 
    setShowColoredText, 
    sortOption, 
    setSortOption 
  } = useContext(ReviewsContext);
  const { isPortrait, showSidebar, handleMenuClick } = useContext(NavigationContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [accountMenuAnchorEl, setAccountMenuAnchorEl] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [signupDate, setSignupDate] = useState(null);
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();

  // Format the signup date for display
  const formattedDate = signupDate ? signupDate.toLocaleDateString() : '';

  // Effect to update rating when selectedBusiness changes
  useEffect(() => {
    if (selectedBusiness && selectedBusiness.rating !== undefined) {
      setRatingValue(selectedBusiness.rating);
    }
  }, [selectedBusiness]);

  // Fetch user info
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          // Store created_at date for tooltip
          if (userData.created_at) {
            setSignupDate(new Date(userData.created_at));
          }
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };
    
    fetchUserInfo();
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccountMenuClick = (event) => {
    setAccountMenuAnchorEl(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        window.location.href = '/login';
      } else {
        console.error('Logout failed');
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      handleClose();
      handleAccountMenuClose();
    }
  };

  const handleManageSubscription = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/payments/customer-portal`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error("No URL returned from customer portal endpoint");
      }
    } catch (error) {
      console.error("Error creating customer portal session:", error);
    }
  };

  const handleTimeframeChange = (event) => {
    // Stop event propagation to prevent document click handler from clearing selections
    if (event && event.nativeEvent) {
      event.nativeEvent.stopImmediatePropagation();
    }
    
    // When timeframe changes, we should reset the month selection
    setSelectedDate(null);
    setTimeframe(event.target.value);
  };

  const handleViewChange = (event, newValue) => {
    // Stop event propagation to prevent document click handler from clearing selections
    if (event && event.nativeEvent) {
      event.nativeEvent.stopImmediatePropagation();
    }
    
    if (newValue !== null) {
      setView(newValue);
    }
  };

  const handleSortOptionChange = (event, newValue) => {
    if (newValue !== null) {
      setSortOption(newValue);
    }
  };

  const handleToggleChange = (event) => {
    // Stop event propagation to prevent document click handler from clearing selections
    if (event && event.nativeEvent) {
      event.nativeEvent.stopImmediatePropagation();
    }
    
    setShowColoredText(!showColoredText);
  };

  const handleUnlockOngoingAnalysis = async () => {
    try {
      // Direct monthly checkout - using the same endpoint as LandingSubscriptions
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/payment-checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plan: 'monthly' }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const { checkout_url } = await response.json();
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
    }
  };

  const isReviewsPage = location.pathname === '/reviews';
  const isDashboardPage = location.pathname === '/';
  const isRelevantPage = isReviewsPage || isDashboardPage;

  return (
    <Box sx={{
      position: 'sticky',
      top: 0,
      zIndex: 10,
      background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
      backdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(25, 118, 210, 0.08)',
      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.03)',
      py: { xs: 1.5, lg: 3 },
      px: { xs: 1.5, lg: 4 }
    }}>
      <Container maxWidth="xl">
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 1, lg: 4 },
          flexWrap: 'nowrap'
        }}>
          {/* Navigation Menu Button - Only in mobile portrait */}
          {!showSidebar && (
            <IconButton
              onClick={handleMenuClick}
              size="small"
              sx={{
                mr: 1,
                display: { xs: 'flex', lg: 'none' },
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                border: '1px solid rgba(25, 118, 210, 0.1)',
                borderRadius: '8px',
                p: 0.75,
                boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                '&:hover': {
                  boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              <MenuIcon sx={{ color: '#546E7A', fontSize: '1.3rem' }} />
            </IconButton>
          )}
          
          {/* Business Info Section */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flex: { xs: '1 1 auto', lg: '0 1 auto' },
            minWidth: 0
          }}>
            <StorefrontIcon sx={{ 
              fontSize: { xs: '1.75rem', lg: '2.5rem' },
              color: '#1976d2',
              flexShrink: 0
            }} />
            <Box sx={{
              minWidth: 0
            }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.25rem', lg: '2rem' },
                  color: '#0A2540',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {selectedBusiness?.name}
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 0.5
              }}>
                {view === "Sales" ? (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <Typography sx={{
                      color: '#0A2540',
                      fontSize: { xs: '0.9rem', lg: '1.1rem' },
                      fontWeight: 600,
                      display: 'block'
                    }}>
                      {currencySymbol}{selectedBusiness?.turnover.toLocaleString()}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <Typography sx={{
                      color: '#0A2540',
                      fontSize: { xs: '0.9rem', lg: '1.1rem' },
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      {selectedBusiness?.rating?.toFixed(1)}
                      <Rating
                        value={ratingValue}
                        precision={0.1}
                        readOnly
                        size="small"
                        sx={{
                          color: '#1976d2',
                          '& .MuiRating-iconEmpty': {
                            color: 'rgba(25, 118, 210, 0.3)'
                          }
                        }}
                      />
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'center',
            gap: 1.5,
            flexShrink: 0
          }}>
            {isRelevantPage && (
              <>
                {/* Sort Toggle - Only on Dashboard page */}
                {isDashboardPage && (
                  <ToggleButtonGroup 
                    value={sortOption}
                    exclusive
                    onChange={handleSortOptionChange}
                    data-filter-exempt="true"
                    sx={{
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                      border: '1px solid rgba(25, 118, 210, 0.1)',
                      borderRadius: '12px !important',
                      boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                      height: '44px',
                      marginRight: '12px',
                      width: { lg: '180px' },
                      '&:hover': {
                        boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                      },
                      '& .MuiToggleButton-root': {
                        border: 'none',
                        fontSize: '0.95rem',
                        fontWeight: 'bold',
                        minWidth: { xs: '60px', lg: '60px' },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        },
                        '&:not(:last-child)': {
                          borderRight: '1px solid rgba(25, 118, 210, 0.2)'
                        }
                      }
                    }}
                  >
                    <Tooltip title="Sort charts by all impacts" arrow>
                      <ToggleButton 
                        value="total" 
                        aria-label="total impact"
                        sx={{
                          color: '#546E7A',
                          fontWeight: 'bold',
                          textTransform: 'capitalize',
                          '&.Mui-selected': {
                            color: '#1976d2',
                          }
                        }}
                      >
                        All
                      </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Sort charts by positive impact" arrow>
                      <ToggleButton 
                        value="positive" 
                        aria-label="positive impact"
                        sx={{
                          color: colors.positiveGreen,
                          fontWeight: 'bold',
                          '&.Mui-selected': {
                            color: colors.positiveGreen,
                          }
                        }}
                      >
                        {view === "Sales" ? `+${currencySymbol}` : "+★"}
                      </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Sort charts by negative impact" arrow>
                      <ToggleButton 
                        value="negative" 
                        aria-label="negative impact"
                        sx={{
                          color: colors.negativeRed,
                          fontWeight: 'bold',
                          '&.Mui-selected': {
                            color: colors.negativeRed,
                          }
                        }}
                      >
                        {view === "Sales" ? `-${currencySymbol}` : "-★"}
                      </ToggleButton>
                    </Tooltip>
                  </ToggleButtonGroup>
                )}

                {/* Analysis toggle - Only on Reviews page */}
                {isReviewsPage && (
                  <Tooltip title="Highlights extracted insights" arrow>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showColoredText}
                          onChange={handleToggleChange}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#1976d2'
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#1976d2'
                            }
                          }}
                        />
                      }
                      label="Insights"
                      sx={{
                        margin: 0,
                        height: '44px',
                        width: '180px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                        border: '1px solid rgba(25, 118, 210, 0.1)',
                        borderRadius: '12px !important',
                        boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                        display: 'flex',
                        marginRight: '12px',
                        px: 1,
                        justifyContent: 'center',
                        '& .MuiFormControlLabel-label': {
                          color: showColoredText ? '#1976d2' : '#78909C',
                          fontWeight: 'bold',
                          fontSize: '0.95rem',
                          ml: 1
                        },
                        '&:hover': {
                          boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                          backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                    />
                  </Tooltip>
                )}

                <ToggleButtonGroup
                  value={view}
                  exclusive
                  onChange={handleViewChange}
                  data-filter-exempt="true"
                  sx={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    borderRadius: '12px !important',
                    boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                    height: '44px',
                    width: { lg: '180px' },
                    marginRight: '12px',
                    '&:hover': {
                      boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    },
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      fontSize: '0.95rem',
                      fontWeight: 'bold',
                      color: '#546E7A',
                      minWidth: { xs: '50%', lg: '90px' },
                      textTransform: 'capitalize',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        color: '#1976d2'
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                      },
                      '&:not(:last-child)': {
                        borderRight: '1px solid rgba(25, 118, 210, 0.2)'
                      }
                    }
                  }}
                >
                  <Tooltip title="Show impact on revenue" arrow>
                    <ToggleButton 
                      value="Sales"
                      data-filter-exempt="true"
                      sx={{
                        color: '#546E7A',
                        fontWeight: 'bold',
                        '&.Mui-selected': {
                          color: '#1976d2',
                        }
                      }}
                    >
                      Revenue
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title="Show impact on rating" arrow>
                    <ToggleButton 
                      value="Rating"
                      data-filter-exempt="true"
                      sx={{
                        color: '#546E7A',
                        fontWeight: 'bold',
                        '&.Mui-selected': {
                          color: '#1976d2',
                        }
                      }}
                    >
                      Rating
                    </ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>

                {/* Timeframe selector with styling like toggle buttons */}
                <Select
                  value={timeframe}
                  onChange={handleTimeframeChange}
                  data-filter-exempt="true"
                  sx={{
                    height: '44px',
                    width: { lg: '180px' },
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    borderRadius: '12px !important',
                    boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                    marginRight: '12px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '& .MuiSelect-select': {
                      fontSize: '0.95rem',
                      fontWeight: 'bold',
                      color: '#546E7A',
                      display: 'flex',
                      alignItems: 'center',
                      pl: 2
                    },
                    '&:hover': {
                      boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: '12px',
                        boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
                        border: '1px solid rgba(25, 118, 210, 0.1)'
                      },
                      "data-filter-exempt": "true"
                    }
                  }}
                >
                  {timeframeOptions.map(option => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      data-filter-exempt="true"
                      onClick={(e) => {
                        // Reset the month selection when choosing a timeframe
                        e.stopPropagation();
                        setSelectedDate(null);
                      }}
                      sx={{
                        fontSize: '0.9rem',
                        py: 1,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          color: '#1976d2',
                          fontWeight: 'bold'
                        }
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}

            <Tooltip title="Account settings" arrow>
              <IconButton
                onClick={handleAccountMenuClick}
                sx={{
                  width: '44px',
                  height: '44px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                  border: '1px solid rgba(25, 118, 210, 0.1)',
                  borderRadius: '12px',
                  boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                  '&:hover': {
                    boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                <SettingsIcon sx={{
                  color: '#546E7A',
                  fontSize: '1.5rem'
                }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Mobile Controls */}
          <Box sx={{
            display: { xs: 'flex', lg: 'none' },
            alignItems: 'center',
            gap: 1,
            flexShrink: 0
          }}>
            {isRelevantPage && (
              <Tooltip title="View options" arrow>
                <IconButton
                  onClick={handleClick}
                  sx={{
                    width: '36px',
                    height: '36px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    borderRadius: '12px',
                    boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                    '&:hover': {
                      boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  <FilterListIcon sx={{
                    color: '#546E7A',
                    fontSize: '1.25rem'
                  }} />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Account settings" arrow>
              <IconButton
                onClick={handleAccountMenuClick}
                sx={{
                  width: '36px',
                  height: '36px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                  border: '1px solid rgba(25, 118, 210, 0.1)',
                  borderRadius: '12px',
                  boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                  '&:hover': {
                    boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                <SettingsIcon sx={{
                  color: '#546E7A',
                  fontSize: '1.25rem'
                }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* View Options Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: '12px',
                boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
                border: '1px solid rgba(25, 118, 210, 0.1)',
                width: '220px'
              },
              'data-filter-exempt': 'true'
            }}
          >
            {isRelevantPage && (
              <>
                {/* Sort Toggle - Only on Dashboard page */}
                {isDashboardPage && (
                  <MenuItem sx={{ py: 1.5, px: 0, display: 'flex', justifyContent: 'center' }}>
                    <ToggleButtonGroup
                      value={sortOption}
                      exclusive
                      onChange={handleSortOptionChange}
                      size="small"
                      data-filter-exempt="true"
                      sx={{
                        width: '180px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                        border: '1px solid rgba(25, 118, 210, 0.1)',
                        borderRadius: '12px !important',
                        boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                        '&:hover': {
                          boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                          backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        },
                        '& .MuiToggleButton-root': {
                          border: 'none',
                          fontSize: '0.95rem',
                          px: 2,
                          minWidth: '60px',
                          fontWeight: 'bold',
                          textTransform: 'capitalize',
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                          },
                          '&:not(:last-child)': {
                            borderRight: '1px solid rgba(25, 118, 210, 0.2)'
                          }
                        }
                      }}
                    >
                      <Tooltip title="Sort charts by all impacts" arrow>
                        <ToggleButton 
                          value="total" 
                          aria-label="total impact"
                          sx={{
                            color: '#546E7A',
                            fontWeight: 'bold',
                            textTransform: 'capitalize',
                            '&.Mui-selected': {
                              color: '#1976d2',
                            }
                          }}
                        >
                          All
                        </ToggleButton>
                      </Tooltip>
                      <Tooltip title="Sort charts by positive impact" arrow>
                        <ToggleButton 
                          value="positive" 
                          aria-label="positive impact"
                          sx={{
                            color: colors.positiveGreen,
                            fontWeight: 'bold',
                            '&.Mui-selected': {
                              color: colors.positiveGreen,
                            }
                          }}
                        >
                          {view === "Sales" ? `+${currencySymbol}` : "+★"}
                        </ToggleButton>
                      </Tooltip>
                      <Tooltip title="Sort charts by negative impact" arrow>
                        <ToggleButton 
                          value="negative" 
                          aria-label="negative impact"
                          sx={{
                            color: colors.negativeRed,
                            fontWeight: 'bold',
                            '&.Mui-selected': {
                              color: colors.negativeRed,
                            }
                          }}
                        >
                          {view === "Sales" ? `-${currencySymbol}` : "-★"}
                        </ToggleButton>
                      </Tooltip>
                    </ToggleButtonGroup>
                  </MenuItem>
                )}

                {/* Analysis toggle - Only on Reviews page */}
                {isReviewsPage && (
                  <MenuItem sx={{ py: 1.5, px: 0, display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Highlights extracted insights" arrow>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showColoredText}
                            onChange={handleToggleChange}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#1976d2'
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#1976d2'
                              }
                            }}
                          />
                        }
                        label="Insights"
                        sx={{
                          margin: 0,
                          height: '44px',
                          width: '180px',
                          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                          border: '1px solid rgba(25, 118, 210, 0.1)',
                          borderRadius: '12px !important',
                          boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                          display: 'flex',
                          marginRight: '12px',
                          px: 1,
                          justifyContent: 'center',
                          '& .MuiFormControlLabel-label': {
                            color: showColoredText ? '#1976d2' : '#78909C',
                            fontWeight: 'bold',
                            fontSize: '0.95rem',
                            ml: 1
                          },
                          '&:hover': {
                            boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                          }
                        }}
                      />
                    </Tooltip>
                  </MenuItem>
                )}

                <MenuItem sx={{ py: 1.5, px: 0, display: 'flex', justifyContent: 'center' }}>
                  <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={handleViewChange}
                    data-filter-exempt="true"
                    sx={{
                      width: '180px',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                      border: '1px solid rgba(25, 118, 210, 0.1)',
                      borderRadius: '12px !important',
                      boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                      '&:hover': {
                        boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                      },
                      '& .MuiToggleButton-root': {
                        border: 'none',
                        fontSize: '0.95rem',
                        px: 2,
                        minWidth: '60px',
                        fontWeight: 'bold',
                        textTransform: 'capitalize',
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        },
                        '&:not(:last-child)': {
                          borderRight: '1px solid rgba(25, 118, 210, 0.2)'
                        }
                      }
                    }}
                  >
                    <Tooltip title="Show impact on revenue" arrow>
                      <ToggleButton 
                        value="Sales"
                        data-filter-exempt="true"
                        sx={{
                          color: '#546E7A',
                          fontWeight: 'bold',
                          '&.Mui-selected': {
                            color: '#1976d2',
                          }
                        }}
                      >
                        Revenue
                      </ToggleButton>
                    </Tooltip>
                    <Tooltip title="Show impact on rating" arrow>
                      <ToggleButton 
                        value="Rating"
                        data-filter-exempt="true"
                        sx={{
                          color: '#546E7A',
                          fontWeight: 'bold',
                          '&.Mui-selected': {
                            color: '#1976d2',
                          }
                        }}
                      >
                        Rating
                      </ToggleButton>
                    </Tooltip>
                  </ToggleButtonGroup>
                </MenuItem>

                {/* Timeframe selector with styling like toggle buttons */}
                <MenuItem sx={{ py: 1.5, px: 0, display: 'flex', justifyContent: 'center' }}>
                  <Select
                    value={timeframe}
                    onChange={handleTimeframeChange}
                    data-filter-exempt="true"
                    sx={{
                      height: '44px',
                      width: '180px',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                      border: '1px solid rgba(25, 118, 210, 0.1)',
                      borderRadius: '12px !important',
                      boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                      },
                      '& .MuiSelect-select': {
                        fontSize: '0.95rem',
                        fontWeight: 'bold',
                        color: '#546E7A',
                        display: 'flex',
                        alignItems: 'center',
                        pl: 2
                      },
                      '&:hover': {
                        boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '12px',
                          boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
                          border: '1px solid rgba(25, 118, 210, 0.1)'
                        },
                        "data-filter-exempt": "true"
                      }
                    }}
                  >
                    {timeframeOptions.map(option => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        data-filter-exempt="true"
                        onClick={(e) => {
                          // Reset the month selection when choosing a timeframe
                          e.stopPropagation();
                          setSelectedDate(null);
                        }}
                        sx={{
                          fontSize: '0.9rem',
                          py: 1,
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            color: '#1976d2',
                            fontWeight: 'bold'
                          }
                        }}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </MenuItem>
              </>
            )}
          </Menu>
        </Box>

        {/* Account Settings Menu */}
        <Menu
          anchorEl={accountMenuAnchorEl}
          open={Boolean(accountMenuAnchorEl)}
          onClose={handleAccountMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: '12px',
              boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
              border: '1px solid rgba(25, 118, 210, 0.1)',
              minWidth: '200px'
            }
          }}
        >
          <MenuItem onClick={handleManageSubscription} sx={{
            p: 2,
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)'
            }
          }}>
            <Typography sx={{ 
              fontWeight: 500,
              fontSize: '0.95rem' 
            }}>
              Manage Subscription
            </Typography>
          </MenuItem>
          
          <MenuItem onClick={handleLogout} sx={{
            p: 2,
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.08)',
              color: colors.negativeRed
            }
          }}>
            <Typography sx={{ 
              fontWeight: 500,
              fontSize: '0.95rem' 
            }}>
              Log Out
            </Typography>
          </MenuItem>
        </Menu>
      </Container>
    </Box>
  );
};

export default Header;